// Nhóm 2–4: Trực page · Telesale · Sale cơ sở. Mỗi nhóm cùng bố cục:
// đầu nhóm → cảnh báo riêng → chỉ số chính (so với kỳ trước) → phân tích → bảng nhân sự.
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { Tooltip } from 'antd';
import { DEPT, fmtFull, fmtMoney, fmtRate, ratio, type Dept, type Insight, type Range, type Snapshot, type Totals } from './insights';
import { handoffs, stageNo, tagIdOf, uncalledCount } from './flow';
import { customerUrl } from './FlowSection';
import StaffTable from './StaffTable';
import { PageTrend, SaleTrend, TeleTrend } from './TrendPanels';
import type { YearTrends } from './useYearTrends';
import { BarList, Delta, GroupAlerts, GroupHeader, KpiGrid, KpiTile, PALETTE, Panel, ProcessStrip, ShareList, SubTitle } from './ui';

// màu theo trạng thái lịch hẹn: 1 Đặt lịch mới, 2 Đã xác nhận, 3 Chờ thực hiện, 4 Đã thực hiện, 0 Hủy lịch
const APPT_COLOR: Record<string, string> = { 1: '#378ADD', 2: '#7F77DD', 3: '#EF9F27', 4: '#1D9E75', 0: '#E24B4A' };

export interface GroupProps {
  cur: Snapshot; prev: Snapshot; c: Totals; p: Totals; alerts: Insight[]; detailUrl: (tab: string) => string; branchNote?: boolean;
  trends: YearTrends; range: Range;
}

const num = (v: number) => Math.round(v).toLocaleString('vi-VN');

const Header = ({ dept, index, subtitle, url }: { dept: Dept; index: number; subtitle: string; url: string }) => (
  <GroupHeader
    index={index} title={DEPT[dept].label} subtitle={subtitle} color={DEPT[dept].color} soft={DEPT[dept].soft}
    extra={<Link to={url} className="flex items-center gap-1 text-xs font-medium text-[#357f96] hover:underline">Báo cáo chi tiết <ArrowRight size={13} /></Link>}
  />
);

const Empty = ({ text = 'Chưa có dữ liệu' }: { text?: string }) => <div className="py-8 text-center text-sm text-slate-400">{text}</div>;

// ============================== 2. TRỰC PAGE ==============================
const CALL_COLOR: Record<string, string> = { 1: '#EF9F27', 2: '#D4537E', 3: '#E24B4A', 4: '#888780', 5: '#378ADD' };
/** "Chưa gọi" (id = null) màu xám, "Thành công" màu xanh, còn lại theo id */
const callColor = (x: { id: number | null; name: string }) => (/chưa gọi/i.test(x.name) ? '#94a3b8' : /thành công/i.test(x.name) ? '#1D9E75' : CALL_COLOR[String(x.id)] || '#7F77DD');

export function PageGroup({ cur, prev, c, p, alerts, detailUrl, branchNote, trends, range }: GroupProps) {
  const color = DEPT.page.color;
  const channels = [...(cur.page?.customer_by_channels || [])].sort((a, b) => b.count - a.count);
  const prevChannels = prev.page?.customer_by_channels || [];
  // P_v2 trả cả 20 thẻ S0→S8; Trực page chỉ phụ trách S0–S4.x (S5 trở đi thuộc Telesale / Sale cơ sở)
  const tags = (cur.page?.customer_by_tags || [])
    .filter(t => { const no = stageNo(t.name); return no === null || no < 5; })
    .sort((a, b) => (stageNo(a.name) ?? a.id) - (stageNo(b.name) ?? b.id));
  const calls = cur.page?.call_result_summary || [];
  const platforms = [...(cur.page?.revenue_by_platforms || [])].sort((a, b) => b.revenue - a.revenue);
  const platformTotal = platforms.reduce((s, x) => s + x.revenue, 0);
  const prevPlatformTotal = (prev.page?.revenue_by_platforms || []).reduce((s, x) => s + x.revenue, 0);
  const staffCount = cur.page?.staff_customer_stats?.length || 0;
  const unc = uncalledCount(cur);
  const s0 = tags.find(t => /^S\s*0\b/i.test(t.name));
  // tổng "khách hàng mới" của API phải bằng tổng cột khách hàng mới ở bảng nhân sự
  const staffNew = (cur.page?.staff_customer_stats || []).reduce((t, s) => t + (Number(s.customer_count) || 0), 0);
  const leadsMismatch = staffNew > 0 && staffNew !== c.leads;
  // chưa có tin nhắn / thẻ / cuộc gọi / doanh thu → 1 thông báo thay cho 4 khung trống
  const noDetail = !channels.length && !tags.some(t => t.count) && !calls.some(x => x.count) && !platformTotal;

  return (
    <>
      <Header dept="page" index={2} subtitle="Tiếp nhận tin nhắn từ fanpage, lấy thông tin khách và đặt lịch" url={detailUrl('page')} />
      {branchNote && <div className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">Trực page chưa hỗ trợ lọc chi nhánh — đang hiển thị toàn hệ thống.</div>}
      <GroupAlerts items={alerts} />
      <ProcessStrip color={color} soft={DEPT.page.soft} steps={[
        { label: 'Khách nhắn tin', value: num(c.chats), sub: `${channels.length} fanpage` },
        { label: 'Lấy được thông tin', value: num(c.leads), sub: `${fmtRate(c.leadRate)} khách nhắn tin` },
        { label: 'Đặt lịch hẹn', value: num(c.pageAppts), sub: `${fmtRate(ratio(c.pageAppts, c.leads))} khách mới` },
        {
          label: 'Bàn giao Telesale', value: unc === null ? '—' : `${num(Math.max(0, c.leads - unc))}/${num(c.leads)}`,
          sub: unc ? `${num(unc)} khách chưa được gọi` : c.leads ? 'đã gọi hết' : 'chưa có khách mới', warn: !!unc, href: unc && s0 ? customerUrl(s0.id, range) : undefined,
        },
      ]} />
      <KpiGrid cols={6}>
        <KpiTile label="Khách nhắn tin" value={num(c.chats)} delta={<Delta cur={c.chats} prev={p.chats} compact />} sub={`${channels.length} fanpage`} />
        <KpiTile label="Khách hàng mới" value={num(c.leads)} delta={<Delta cur={c.leads} prev={p.leads} compact />} sub="đã lấy được thông tin" />
        <KpiTile label="Tỷ lệ lấy thông tin" value={fmtRate(c.leadRate)} delta={<Delta cur={c.leadRate} prev={p.leadRate} kind="points" compact />} sub="khách mới / khách nhắn tin" />
        <KpiTile label="Lịch hẹn tạo" value={num(c.pageAppts)} delta={<Delta cur={c.pageAppts} prev={p.pageAppts} compact />} sub={`${fmtRate(ratio(c.pageAppts, c.leads))} khách mới`} />
        <KpiTile label="Doanh thu từ nền tảng" value={<Tooltip title={fmtFull(platformTotal)}>{fmtMoney(platformTotal)}</Tooltip>} delta={<Delta cur={platformTotal} prev={prevPlatformTotal} compact />} sub={`${platforms.length} nền tảng`} />
        <KpiTile label="Nhân sự trực page" value={staffCount} sub={staffCount ? `TB ${num(c.chats / staffCount)} khách chat/người` : undefined} />
      </KpiGrid>

      {leadsMismatch && (
        <div className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Tổng khách hàng mới <b>{num(c.leads)}</b> lệch với bảng nhân sự (<b>{num(staffNew)}</b>) — số tổng của API cần đối soát.
        </div>
      )}

      {noDetail ? (
        <div className="mb-4 flex flex-col items-center gap-1 rounded-xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center">
          <div className="text-sm font-medium text-slate-600">Chưa có dữ liệu tin nhắn fanpage trong kỳ</div>
          <div className="max-w-xl text-xs text-slate-400">Khách theo fanpage, trạng thái khách S0 – S4, kết quả gọi và doanh thu theo nền tảng sẽ hiện khi fanpage được kết nối và có khách nhắn tin.</div>
        </div>
      ) : (
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-4">
        <Panel title="Khách theo fanpage" extra={<span className="text-xs text-slate-400">{num(channels.reduce((s, x) => s + x.count, 0))} khách</span>}>
          <BarList
            color={color}
            items={channels.slice(0, 8).map((ch, i) => {
              const pv = prevChannels.find(x => x.name === ch.name)?.count;
              return { key: `${ch.name}-${i}`, label: ch.name, value: ch.count, display: num(ch.count), title: pv !== undefined ? `${ch.name}: ${num(ch.count)} (kỳ trước ${num(pv)})` : ch.name };
            })}
          />
          {channels.length > 8 && <div className="mt-2 text-xs text-slate-400">+{channels.length - 8} fanpage khác</div>}
        </Panel>
        <Panel title="Trạng thái khách hàng" info="Phân bố khách theo thẻ chăm sóc (mức độ quan tâm). Bấm tên thẻ để mở danh sách khách.">
          <ShareList items={tags.map((t, i) => ({ key: String(t.id), label: t.name, value: t.count, color: t.color || PALETTE[i % PALETTE.length], title: t.description || t.name, href: customerUrl(t.id, range) }))} collapseZero />
        </Panel>
        <Panel title="Kết quả gọi khách" info="Kết quả liên hệ những khách do Trực page tạo.">
          <ShareList items={calls.map((x, i) => ({ key: `${x.id}-${i}`, label: x.name, value: x.count, color: callColor(x) }))} />
        </Panel>
        <Panel title="Doanh thu theo nền tảng" info="Doanh thu từ khách có nguồn là từng nền tảng." extra={<span className="text-xs text-slate-500">Tổng <b className="text-slate-800">{fmtMoney(platformTotal)}</b></span>}>
          <BarList color={color} items={platforms.map(x => ({ key: String(x.id), label: x.name, value: x.revenue, display: `${fmtMoney(x.revenue)} · ${fmtRate(ratio(x.revenue, platformTotal))}`, title: `${x.name}: ${fmtFull(x.revenue)}` }))} />
        </Panel>
      </div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <PageTrend t={trends} range={range} />
        <StaffTable cur={cur} c={c} dept="page" range={range} className="xl:col-span-2" />
      </div>
    </>
  );
}

// ============================== 3. TELESALE ==============================
export function TeleGroup({ cur, prev, c, p, alerts, detailUrl, trends, range }: GroupProps) {
  const color = DEPT.tele.color;
  const calls = [...(cur.tele?.call_result_summary || [])].sort((a, b) => b.total - a.total);
  const tags = [...(cur.tele?.tag_summary || [])];
  const services = [...(cur.tele?.service_rankings || [])].sort((a, b) => b.booked - a.booked);
  const rev = cur.tele?.revenue;
  const [inbound, outbound] = handoffs(cur, prev);

  return (
    <>
      <Header dept="tele" index={3} subtitle="Gọi điện chăm sóc, đặt lịch hẹn và đưa khách đến cơ sở" url={detailUrl('telesale')} />
      <GroupAlerts items={alerts} />
      <ProcessStrip color={color} soft={DEPT.tele.soft} steps={[
        {
          label: 'Nhận từ Trực page', value: num(inbound.input), sub: inbound.backlog ? `${num(inbound.backlog)} chưa gọi` : inbound.input ? 'đã gọi hết' : 'chưa có khách được giao', warn: inbound.backlog > 0,
          href: inbound.backlog && inbound.tagId ? customerUrl(inbound.tagId, range) : undefined,
        },
        { label: 'Đặt lịch hẹn', value: num(c.booked), sub: `${(cur.tele?.rankings || []).filter(r => Number(r.booked) > 0).length} nhân viên có lịch` },
        { label: 'Khách đến', value: num(c.teleShowup), sub: `${fmtRate(c.showupRate)} · ${num(outbound.backlog)} chưa đến`, warn: outbound.health === 'bad' },
        { label: 'Bàn giao Sale cơ sở → chốt', value: num(c.teleClosed), sub: `${fmtRate(c.teleCloseRate)} khách đến` },
      ]} />
      <KpiGrid cols={6}>
        <KpiTile label="Lịch hẹn đã đặt" value={num(c.booked)} delta={<Delta cur={c.booked} prev={p.booked} compact />} />
        <KpiTile label="Khách đến" value={num(c.teleShowup)} delta={<Delta cur={c.teleShowup} prev={p.teleShowup} compact />} />
        <KpiTile label="Tỷ lệ khách đến" value={fmtRate(c.showupRate)} delta={<Delta cur={c.showupRate} prev={p.showupRate} kind="points" compact />} sub="khách đến / lịch đặt" />
        <KpiTile label="Khách chốt" value={num(c.teleClosed)} delta={<Delta cur={c.teleClosed} prev={p.teleClosed} compact />} />
        <KpiTile label="Tỷ lệ chốt" value={fmtRate(c.teleCloseRate)} delta={<Delta cur={c.teleCloseRate} prev={p.teleCloseRate} kind="points" compact />} sub="khách chốt / khách đến" />
        <KpiTile
          // BE chưa xác nhận đây là tiền đã thu: số này lớn hơn nhiều doanh thu Sale cơ sở → gọi là giá trị dịch vụ đã đặt
          label="Giá trị dịch vụ đã đặt" info="Tổng giá dịch vụ của các lịch Telesale đặt trong kỳ (chưa phải tiền đã thu). Doanh thu thật xem ở nhóm Sale cơ sở." value={rev ? <Tooltip title={fmtFull(rev.current_period)}>{fmtMoney(rev.current_period)}</Tooltip> : '—'}
          // revenue.prev_period của BE là cả kỳ liền trước (vd. trọn tháng trước) — so với kỳ so sánh của dashboard cho đồng nhất
          delta={rev && prev.tele ? <Delta cur={rev.current_period} prev={p.teleRevenue} compact /> : undefined}
          sub={rev && prev.tele ? `kỳ trước ${fmtMoney(p.teleRevenue)}` : undefined}
        />
      </KpiGrid>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel title="Kết quả cuộc gọi" info="Kết quả gọi gần nhất của từng khách mới trong kỳ." extra={<span className="text-xs text-slate-400">{num(calls.reduce((s, x) => s + x.total, 0))} khách</span>}>
          <ShareList items={calls.map((x, i) => ({ key: `${x.name}-${i}`, label: x.name, value: x.total, color: /chưa gọi|thành công/i.test(x.name) ? callColor({ id: null, name: x.name }) : PALETTE[(i + 1) % PALETTE.length] }))} empty="Chưa có dữ liệu cuộc gọi" />
        </Panel>
        <Panel title="Khách theo thẻ chăm sóc" info="Số khách Telesale đang chăm sóc theo từng mức (thẻ).">
          {tags.length ? (
            <BarList color={color} items={tags.map((t, i) => { const id = tagIdOf(t.code); return { key: `${t.code || t.tag}-${i}`, label: t.tag, value: t.total, display: num(t.total), title: t.desc || t.tag, href: id ? customerUrl(id, range) : undefined }; })} collapseZero />
          ) : <Empty />}
        </Panel>
        <Panel title="Dịch vụ được đặt lịch nhiều nhất" info="Tính theo lượt dịch vụ: một lịch hẹn đặt nhiều dịch vụ được tính nhiều lượt.">

          {services.length ? (
            <ul className="space-y-2">
              {services.slice(0, 7).map(s => (
                <li key={s.code || s.name} className="text-[13px]" title={s.name}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-slate-600">{s.name}</span>
                    <span className="shrink-0"><b className="text-slate-800">{num(s.booked)}</b> <span className="text-xs text-slate-400">lượt · {num(s.closed)} chốt</span></span>
                  </div>
                  <span className="mt-0.5 block h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <span className="block h-full rounded-full" style={{ width: `${(s.booked / Math.max(1, services[0].booked)) * 100}%`, background: color }} />
                  </span>
                </li>
              ))}
            </ul>
          ) : <Empty />}
        </Panel>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <TeleTrend t={trends} range={range} />
        <StaffTable cur={cur} c={c} dept="tele" className="xl:col-span-2" />
      </div>
    </>
  );
}

// ============================== 4. SALE CƠ SỞ ==============================
export function SaleGroup({ cur, c, p, alerts, detailUrl, trends, range }: GroupProps) {
  const color = DEPT.sale.color;
  const traffic = cur.sale?.traffic_statistics;
  const trafficData = (traffic?.hours || []).map((h, i) => ({ time: h, today: Number(traffic?.today?.[i]) || 0, yesterday: Number(traffic?.yesterday?.[i]) || 0 }));
  const statuses = cur.sale?.status_summary || [];
  const services = [...(cur.sale?.service_summary || [])].sort((a, b) => b.revenue - a.revenue);
  const svcTotal = services.reduce((s, x) => s + x.revenue, 0);
  const statusOf = (re: RegExp) => statuses.find(x => re.test(x.name));
  const waiting = statusOf(/chờ|đợi/i);
  const doing = statusOf(/đang làm/i);
  const done = statusOf(/hoàn thành/i);
  const appt = (cur.appt || []).filter(s => s.status !== 'all');
  const apptTotal = appt.reduce((t, s) => t + s.count, 0);
  const apptDone = appt.find(s => String(s.status) === '4')?.count ?? 0;
  // hai API đếm khác nhau (lịch theo ngày hẹn, khách đến theo lịch đã gán Sale) → chỉ là chênh lệch để đối soát,
  // không phải danh sách lịch cụ thể
  const showupGap = appt.length ? Math.max(0, apptDone - c.showup) : 0;
  // trạng thái tại cơ sở từ API + phần khách đến còn lại (chưa chốt, chưa vào trạng thái nào)
  const onsite = statuses.map((s, i) => ({ href: tagIdOf(s.name) ? customerUrl(tagIdOf(s.name)!, range) : undefined, key: `${s.name}-${i}`, label: s.name, value: s.total, color: PALETTE[i % PALETTE.length], title: s.tag ? `${s.name} · ${s.tag}` : s.name }));
  const rest = c.showup - statuses.reduce((t, s) => t + s.total, 0);
  if (rest > 0) onsite.push({ href: undefined, key: 'rest', label: 'Đã đến, chưa chốt', value: rest, color: '#94a3b8', title: 'Khách đến cơ sở chưa thuộc trạng thái nào ở trên' });

  return (
    <>
      <Header dept="sale" index={4} subtitle="Tiếp khách tại cơ sở, tư vấn và chốt dịch vụ" url={detailUrl('onsite')} />
      <GroupAlerts items={alerts} />
      <ProcessStrip color={color} soft={DEPT.sale.soft} steps={[
        { label: 'Khách đến cơ sở', value: num(c.showup), sub: c.teleShowup ? `Telesale ghi nhận ${num(c.teleShowup)}` : undefined, warn: !!c.teleShowup && Math.abs(c.teleShowup - c.showup) > Math.max(c.teleShowup, c.showup) * 0.2 },
        { label: 'Chờ tư vấn / làm', value: waiting ? num(waiting.total) : '—', sub: doing ? `${num(doing.total)} đang làm dịch vụ` : undefined, href: tagIdOf(doing?.name) ? customerUrl(tagIdOf(doing?.name)!, range) : undefined },
        { label: 'Chốt dịch vụ', value: num(c.closed), sub: `${fmtRate(c.closeRate)} khách đến` },
        { label: 'Hoàn thành & doanh thu', value: fmtMoney(c.revenue), sub: done ? `${num(done.total)} khách hoàn thành` : undefined, href: tagIdOf(done?.name) ? customerUrl(tagIdOf(done?.name)!, range) : undefined },
      ]} />
      <KpiGrid cols={6}>
        <KpiTile label="Khách đến cơ sở" value={num(c.showup)} delta={<Delta cur={c.showup} prev={p.showup} compact />} />
        <KpiTile label="Khách chốt" value={num(c.closed)} delta={<Delta cur={c.closed} prev={p.closed} compact />} />
        <KpiTile label="Tỷ lệ chốt" value={fmtRate(c.closeRate)} delta={<Delta cur={c.closeRate} prev={p.closeRate} kind="points" compact />} />
        <KpiTile label="Doanh thu" value={<Tooltip title={fmtFull(c.revenue)}>{fmtMoney(c.revenue)}</Tooltip>} delta={<Delta cur={c.revenue} prev={p.revenue} compact />} />
        <KpiTile label="Đơn trung bình" value={<Tooltip title={fmtFull(c.aov)}>{fmtMoney(c.aov)}</Tooltip>} delta={<Delta cur={c.aov} prev={p.aov} compact />} />
        <KpiTile label="Khách đến hôm nay" value={traffic ? num(traffic.summary.total_today) : '—'} delta={traffic ? <Delta cur={traffic.summary.total_today} prev={traffic.summary.total_yesterday} compact /> : undefined} sub={traffic ? `hôm qua ${num(traffic.summary.total_yesterday)}` : undefined} />
      </KpiGrid>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel title="Khách đến theo giờ hôm nay" info="So với cùng giờ hôm qua. Luôn là ngày hiện tại, không theo kỳ đang chọn.">
          {trafficData.length ? (
            <>
              <div className="mb-1 flex gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1.5"><span className="w-4 border-t-2" style={{ borderColor: color }} />Hôm nay</span>
                <span className="flex items-center gap-1.5"><span className="w-4 border-t-2 border-dashed border-slate-400" />Hôm qua</span>
              </div>
              <div className="h-[230px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trafficData} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#eef2f5" />
                    <XAxis dataKey="time" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} minTickGap={12} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <ChartTooltip formatter={(v, k) => [`${v} khách`, k === 'today' ? 'Hôm nay' : 'Hôm qua']} />
                    <Line dataKey="yesterday" stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={1.5} dot={false} />
                    <Line dataKey="today" stroke={color} strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : <Empty text="Chưa có dữ liệu khách đến hôm nay" />}
        </Panel>
        <Panel
          title="Trạng thái khách đến cơ sở"
          info="Lịch hẹn: theo trạng thái lịch trong kỳ. Tại cơ sở: khách đã được gán Sale cơ sở tiếp."
          extra={<Link to="/appointments" className="flex items-center gap-1 text-xs font-medium text-[#357f96] hover:underline">Xem lịch hẹn <ArrowRight size={13} /></Link>}
        >
          {appt.length > 0 && (
            <>
              <SubTitle right={<><b className="text-slate-700">{num(apptTotal)}</b> lịch</>}>Lịch hẹn trong kỳ</SubTitle>
              <ShareList items={appt.map(s => ({ key: `appt-${s.status}`, label: s.text || String(s.status), value: s.count, color: APPT_COLOR[String(s.status)] || '#94a3b8' }))} />
            </>
          )}
          <SubTitle right={<><b className="text-slate-700">{num(c.showup)}</b> khách</>}>Tại cơ sở</SubTitle>
          <ShareList items={onsite} empty="Chưa có khách được Sale cơ sở tiếp" />
          {showupGap > 0 && (
            <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <b>{num(apptDone)} lịch đã thực hiện</b> nhưng chỉ <b>{num(c.showup)} khách đến</b> được Sale cơ sở ghi nhận (chênh {num(showupGap)}).
              Thường do lịch chưa gán Sale cơ sở, hoặc một khách có nhiều lịch — cần đối soát.
            </div>
          )}
        </Panel>
        <Panel title="Dịch vụ doanh thu cao" extra={<span className="text-xs text-slate-500">Tổng <b className="text-slate-800">{fmtMoney(svcTotal)}</b></span>}>
          <BarList color={color} items={services.slice(0, 7).map(s => ({ key: s.code || s.name, label: s.name, value: s.revenue, display: `${fmtMoney(s.revenue)} · ${num(s.total)} lượt`, title: `${s.name}: ${fmtFull(s.revenue)}` }))} />
        </Panel>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <SaleTrend t={trends} range={range} />
        <StaffTable cur={cur} c={c} dept="sale" className="xl:col-span-2" />
      </div>
    </>
  );
}
